import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  Box,
  Button,
  TextField,
  Typography,
  CircularProgress,
  Alert,
  Paper,
  Container,
} from "@mui/material";
import useSiteTitleDesc from "../../hooks/useSiteTitleDesc";
import BackBtn from "../../widgets/BackBtn";
import { getDbHeader, getHeader } from "../../utils/util";
import { ADMIN, API_ENDPOINT } from "../../utils/constants";

const AppMetadataEditor = () => {
  const { data, isLoading: apiLoading } = useSiteTitleDesc();
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState("");

  // Populate fields with API data when available and update document
  useEffect(() => {
    if (data) {
      setTitle(data.siteTitle);
      setDescription(data.siteDescription);

      // Update document immediately with API data
      document.title = data.siteTitle;
      let metaDescription = document.querySelector('meta[name="description"]');
      if (!metaDescription) {
        metaDescription = document.createElement("meta");
        metaDescription.name = "description";
        document.head.appendChild(metaDescription);
      }
      metaDescription.setAttribute("content", data.siteDescription);
    }
  }, [data]);

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setMessage("");

    try {
      const response = await axios.post(
        `${API_ENDPOINT}event/update-metadata`,
        {
          title,
          description,
        },
        getDbHeader()
      );

      // Update document title and meta description
      document.title = title;
      let metaDescription = document.querySelector('meta[name="description"]');
      if (!metaDescription) {
        metaDescription = document.createElement("meta");
        metaDescription.name = "description";
        document.head.appendChild(metaDescription);
      }
      metaDescription.setAttribute("content", description);

      setMessage("Metadata updated successfully!");
    } catch (error) {
      setMessage(`Error updating metadata: ${error.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Container>
      <BackBtn />
      <Container maxWidth="sm">
        <Paper elevation={3} sx={{ p: 4, mt: 4 }}>
          <Typography variant="h5" component="h2" mb={3}>
            Update App Metadata
          </Typography>

          {apiLoading ? (
            <Box display="flex" justifyContent="center" my={2}>
              <CircularProgress />
            </Box>
          ) : (
            <form onSubmit={handleSubmit}>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
                <TextField
                  size="small"
                  label="App Title"
                  variant="outlined"
                  fullWidth
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="Enter app title"
                  required
                  disabled={isLoading}
                  InputLabelProps={{ shrink: true }}
                />

                <TextField
                  label="App Description"
                  variant="outlined"
                  fullWidth
                  multiline
                  rows={4}
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Enter app description"
                  required
                  disabled={isLoading}
                  InputLabelProps={{ shrink: true }}
                />

                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  disabled={isLoading || apiLoading}
                  startIcon={
                    isLoading ? (
                      <CircularProgress size={20} color="inherit" />
                    ) : null
                  }
                  sx={{ mt: 2 }}
                >
                  {isLoading ? "Updating..." : "Update Metadata"}
                </Button>

                {message && (
                  <Alert
                    severity={message.includes("Error") ? "error" : "success"}
                    sx={{ mt: 2 }}
                  >
                    {message}
                  </Alert>
                )}
              </Box>
            </form>
          )}
        </Paper>
      </Container>
    </Container>
  );
};

export default AppMetadataEditor;
